import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Zap, Plus, History } from 'lucide-react';
import { User, getCurrentUser } from '@/services/authService';
import { cn } from '@/lib/utils';

interface CreditDisplayProps {
  compact?: boolean;
  className?: string;
  credits?: number;
  creditsUsed?: number;
}

export function CreditDisplay({ compact = false, className, credits, creditsUsed }: CreditDisplayProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLowCreditWarning, setShowLowCreditWarning] = useState(false);

  useEffect(() => {
    if (credits !== undefined) {
      setLoading(false);
      setShowLowCreditWarning(credits < 10);
      return;
    }

    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        setUser(userData);
        
        if (userData && userData.ai_credits && userData.ai_credits < 10) {
          setShowLowCreditWarning(true);
        } else {
          setShowLowCreditWarning(false);
        }
      } catch (error) {
        console.error('Lỗi khi lấy thông tin user:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    
    const intervalId = setInterval(fetchUserData, 60000);
    
    return () => clearInterval(intervalId);
  }, [credits]);

  if (loading) {
    return (
      <div className={cn(
        "bg-muted animate-pulse rounded-md",
        compact ? "h-7 w-16" : "h-9 w-24",
        className
      )}></div>
    );
  }

  if (!user && credits === undefined) {
    return null;
  }

  const creditValue = credits !== undefined ? credits : (user?.ai_credits || 0);
  const creditUsedValue = creditsUsed !== undefined ? creditsUsed : (user?.ai_credits_used || 0);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size={compact ? "xs" : "sm"}
            className={cn(
              "flex items-center gap-1",
              showLowCreditWarning ? "border-red-500 text-red-500 hover:text-red-500 hover:border-red-500" : "",
              compact ? "h-7 px-2 text-xs" : "",
              className
            )}
            // onClick={() => navigate('/credits/history')}
          >
            <Zap className={cn(
              compact ? "h-3 w-3" : "h-4 w-4",
              showLowCreditWarning ? "text-red-500" : "text-primary"
            )} />
            <span className="font-medium">{creditValue}</span>
          </Button>
        </TooltipTrigger>
        {/* <TooltipContent>
          <div className="space-y-2 p-1">
            <p className="font-semibold">{t('credits.balance')}: {creditValue}</p>
            {creditUsedValue > 0 && (
              <p className="text-xs text-muted-foreground">{t('credits.used')}: {creditUsedValue}</p>
            )}
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 px-2 text-xs"
                onClick={() => navigate('/subscription')}
              >
                <Plus className="h-3 w-3 mr-1" />
                {t('credits.add')}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 px-2 text-xs"
                onClick={() => navigate('/credits/history')}
              >
                <History className="h-3 w-3 mr-1" />
                {t('credits.history.view')}
              </Button>
            </div>
            {showLowCreditWarning && (
              <p className="text-xs text-red-500 font-medium">
                {t('credits.lowBalance')}
              </p>
            )}
          </div>
        </TooltipContent> */}
      </Tooltip>
    </TooltipProvider>
  );
} 